import { Badge } from '@repo/ui/components/badge';
import { Button } from '@repo/ui/components/button';
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@repo/ui/components/card';
import { cn } from '@repo/ui/lib/cn';
import { Link } from '@tanstack/react-router';
import { Check, X } from 'lucide-react';
import { useState } from 'react';

import { landingContent } from '@/constants/landing-content';

const { pricing } = landingContent;

export function PricingSection() {
  const [isYearly, setIsYearly] = useState(false);

  return (
    <section id={pricing.id} className="bg-muted/30 py-16 md:py-24">
      <div className="container mx-auto max-w-6xl px-4">
        <h2 className="text-foreground text-center text-3xl font-bold tracking-tight md:text-4xl">
          {pricing.headline}
        </h2>
        <p className="text-muted-foreground mt-3 text-center text-lg">
          {pricing.subheadline}
        </p>

        {/* Billing Toggle */}
        <div className="mt-8 flex items-center justify-center gap-3">
          <span
            className={cn(
              'text-sm font-medium transition-colors',
              !isYearly ? 'text-foreground' : 'text-muted-foreground',
            )}
          >
            {pricing.billingToggle.monthly}
          </span>
          <button
            type="button"
            role="switch"
            aria-checked={isYearly}
            onClick={() => setIsYearly(!isYearly)}
            className={cn(
              'relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors',
              'focus-visible:ring-ring focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none',
              isYearly ? 'bg-primary' : 'bg-muted',
            )}
          >
            <span
              className={cn(
                'bg-background pointer-events-none block h-5 w-5 rounded-full shadow-lg ring-0 transition-transform',
                isYearly ? 'translate-x-5' : 'translate-x-0',
              )}
            />
          </button>
          <span
            className={cn(
              'text-sm font-medium transition-colors',
              isYearly ? 'text-foreground' : 'text-muted-foreground',
            )}
          >
            {pricing.billingToggle.yearly}
          </span>
        </div>

        <div className="mt-12 grid gap-8 md:grid-cols-2 md:gap-6 lg:mx-auto lg:max-w-4xl">
          {pricing.plans.map((plan, index) => {
            const isPro = plan.highlighted;
            const price = isPro
              ? isYearly
                ? plan.yearlyPrice
                : plan.monthlyPrice
              : plan.price;
            const priceDetail = isPro
              ? isYearly
                ? plan.yearlyPriceDetail
                : plan.monthlyPriceDetail
              : plan.priceDetail;

            return (
              <Card
                key={index}
                className={cn(
                  'relative',
                  plan.highlighted
                    ? 'border-primary border-2 shadow-lg'
                    : 'border shadow-sm',
                )}
              >
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-2xl">{plan.name}</CardTitle>
                    {plan.badge && (
                      <Badge
                        variant="secondary"
                        className="bg-primary text-primary-foreground"
                      >
                        {plan.badge}
                      </Badge>
                    )}
                  </div>
                  <div>
                    <p className="text-foreground text-3xl font-bold">
                      {price}
                      <span className="text-muted-foreground text-base font-normal">
                        {priceDetail}
                      </span>
                    </p>
                    <p className="text-primary mt-1 h-4 text-sm font-medium">
                      {isPro &&
                        isYearly &&
                        plan.yearlySavings &&
                        plan.yearlySavings}
                    </p>
                  </div>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    {plan.features.map((feature, featureIndex) => (
                      <li
                        key={featureIndex}
                        className="flex items-center gap-2"
                      >
                        <Check className="text-primary h-5 w-5 shrink-0" />
                        <span className="text-muted-foreground">{feature}</span>
                      </li>
                    ))}
                    {plan.limitations?.map((limitation, limitIndex) => (
                      <li key={limitIndex} className="flex items-center gap-2">
                        <X className="text-muted-foreground h-5 w-5 shrink-0" />
                        <span className="text-muted-foreground/60">
                          {limitation}
                        </span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
                <CardFooter>
                  <Button
                    asChild
                    variant={plan.ctaVariant}
                    className="w-full"
                    size="lg"
                  >
                    <Link to="/registration">{plan.cta}</Link>
                  </Button>
                </CardFooter>
              </Card>
            );
          })}
        </div>
      </div>
    </section>
  );
}
